// Shared IP address utility — used by Insights, Contact page, and Navbar profile.
// Fetches the IP once, caches it, and lets every consumer read from cache.
import { fetIpAddress } from "./Api/api";

const IP_CACHE_KEY = "ticca_ip_cache";
const IP_CACHE_TTL = 30 * 60 * 1000; // 30 minutes

// Backup IP services if the primary (ip.ticca.com) fails
const BACKUP_IP_SERVICES = [
  "https://api.ipify.org/?format=json",
  "https://api.ip.sb/geoip",
];

let cachedIpAddress: string | null = null;
let ipAddressPromise: Promise<string> | null = null;

/** Fetches IP from the primary service, then falls back to backups. */
function fetchIpFromNetwork(): Promise<string> {
  const tryService = (url: string, parser: (data: unknown) => string) =>
    // Use revalidation-friendly mode so requests remain BFcache-compatible.
    fetch(url, { method: "GET", cache: "no-cache" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => {
        const ip = parser(data);
        if (ip && /^\d+\.\d+\.\d+\.\d+$/.test(ip)) return ip;
        return Promise.reject(new Error("invalid ip"));
      });

  // Primary: ip.ticca.com (axios-based, returns raw text IP)
  return fetIpAddress()
    .then(({ data }) => String(data ?? "").trim())
    .then((ip) => {
      if (/^\d+\.\d+\.\d+\.\d+$/.test(ip)) return ip;
      throw new Error("invalid primary ip");
    })
    .catch(() =>
      // Backup 1: ipify
      tryService(BACKUP_IP_SERVICES[0], (d) =>
        String((d as { ip?: string }).ip ?? ""),
      ).catch(() =>
        // Backup 2: ip.sb
        tryService(BACKUP_IP_SERVICES[1], (d) =>
          String((d as { ip?: string }).ip ?? ""),
        ),
      ),
    );
}

function readCache(): string | null {
  try {
    const raw = sessionStorage.getItem(IP_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ip || !parsed?.ts) return null;
    if (Date.now() - parsed.ts > IP_CACHE_TTL) return null;
    return String(parsed.ip);
  } catch {
    return null;
  }
}

function writeCache(ip: string): void {
  try {
    sessionStorage.setItem(
      IP_CACHE_KEY,
      JSON.stringify({ ip, ts: Date.now() }),
    );
  } catch {
    // ignore storage errors
  }
}

/**
 * Returns the cached IP synchronously (if available).
 * Use this for instant reads (e.g. profile dropdown render).
 */
export function getCachedIp(): string | null {
  if (cachedIpAddress !== null) return cachedIpAddress;
  const fromStorage = readCache();
  if (fromStorage !== null) {
    cachedIpAddress = fromStorage;
    return fromStorage;
  }
  return null;
}

/**
 * Eagerly starts the IP fetch at module load so it resolves before first use.
 * Returns a promise that resolves to the IP (or "null" on failure).
 */
export const ipAddressLoading: Promise<string> = (() => {
  const cached = getCachedIp();
  if (cached !== null) {
    return Promise.resolve(cached);
  }
  return fetchIpFromNetwork()
    .then((ip) => {
      const resolved = ip || "null";
      cachedIpAddress = resolved;
      writeCache(resolved);
      return resolved;
    })
    .catch(() => {
      cachedIpAddress = "null";
      return "null";
    });
})();

/**
 * Returns the IP, preferring cache. If not cached, waits for the in-flight
 * fetch (or starts one). Never throws — falls back to "null".
 */
export const fetchIpAddress = async (): Promise<string> => {
  const cached = getCachedIp();
  if (cached !== null) return cached;
  if (ipAddressPromise) return ipAddressPromise;
  return ipAddressLoading;
};

/**
 * Retries fetching the IP up to `maxAttempts` times (for slow networks).
 * Each attempt has a `timeoutMs` cap. Returns the IP or "" if exhausted.
 */
export const fetchIpAddressWithRetry = async (
  maxAttempts = 4,
  timeoutMs = 3000,
  gapMs = 1500,
): Promise<string> => {
  const cached = getCachedIp();
  if (cached !== null && cached !== "null") return cached;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const ip = await Promise.race([
        fetchIpFromNetwork(),
        new Promise<string>((resolve) =>
          setTimeout(() => resolve(""), timeoutMs),
        ),
      ]);
      if (ip && /^\d+\.\d+\.\d+\.\d+$/.test(ip)) {
        cachedIpAddress = ip;
        writeCache(ip);
        return ip;
      }
    } catch {
      // fall through to retry
    }
    if (attempt < maxAttempts) {
      await new Promise((r) => setTimeout(r, gapMs));
    }
  }
  return "";
};